import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ShareNetwork, 
  X, 
  Copy, 
  CheckCircle,
  WhatsappLogo,
  TwitterLogo,
  FacebookLogo,
  LinkedinLogo,
  EnvelopeSimple,
  ChatCircle
} from '@phosphor-icons/react';

const ShareButton = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const shareUrl = window.location.origin;
  const shareTitle = "Safe Space by Adeseun";
  const shareText = "I found this amazing peer support platform for mental health. Check it out - you're not alone! 💚";

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          text: shareText,
          url: shareUrl,
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.log('Share failed:', err);
        }
      }
    } else {
      setIsOpen(true);
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.log('Copy failed:', err);
    }
  };

  const shareOptions = [
    {
      name: 'WhatsApp',
      icon: WhatsappLogo,
      color: '#25D366',
      url: `https://wa.me/?text=${encodeURIComponent(`${shareText} ${shareUrl}`)}`
    },
    {
      name: 'Twitter',
      icon: TwitterLogo,
      color: '#1DA1F2',
      url: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`
    },
    {
      name: 'Facebook',
      icon: FacebookLogo,
      color: '#1877F2',
      url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`
    },
    {
      name: 'LinkedIn',
      icon: LinkedinLogo,
      color: '#0A66C2',
      url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`
    },
    {
      name: 'Email',
      icon: EnvelopeSimple,
      color: '#B46C4A',
      url: `mailto:?subject=${encodeURIComponent(shareTitle)}&body=${encodeURIComponent(`${shareText}\n\n${shareUrl}`)}`
    },
    {
      name: 'SMS',
      icon: ChatCircle,
      color: '#647B64',
      url: `sms:?body=${encodeURIComponent(`${shareText} ${shareUrl}`)}`
    }
  ];

  return (
    <>
      {/* Floating Share Button */}
      <motion.button
        onClick={handleNativeShare}
        className="fixed bottom-6 left-6 z-[9998] w-14 h-14 bg-[#647B64] hover:bg-[#546854] text-white rounded-full shadow-lg flex items-center justify-center transition-all duration-300"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        data-testid="share-btn"
        title="Share Safe Space"
      >
        <ShareNetwork size={24} weight="fill" />
      </motion.button>

      {/* Share Modal */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[9999] flex items-end sm:items-center justify-center p-4"
            onClick={() => setIsOpen(false)}
          >
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 50 }}
              className="bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl w-full max-w-md overflow-hidden"
              onClick={(e) => e.stopPropagation()}
              data-testid="share-modal"
            >
              {/* Header */}
              <div className="p-6 border-b border-[#E5DCD0]">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-['Outfit'] font-semibold text-[#2A2825]">
                    Share Safe Space
                  </h3>
                  <button
                    onClick={() => setIsOpen(false)}
                    className="w-8 h-8 bg-[#F2EBE1] hover:bg-[#E5DCD0] rounded-full flex items-center justify-center transition-colors"
                  >
                    <X size={18} className="text-[#5C564F]" />
                  </button>
                </div>
                <p className="text-sm text-[#5C564F] mt-2">
                  Help someone who might need support by sharing this platform.
                </p>
              </div>

              {/* Copy Link */}
              <div className="p-4 border-b border-[#E5DCD0]">
                <div className="flex items-center gap-3 bg-[#F9F6F0] rounded-xl p-3">
                  <input
                    type="text"
                    value={shareUrl}
                    readOnly
                    className="flex-1 bg-transparent text-sm text-[#5C564F] outline-none"
                  />
                  <button
                    onClick={handleCopyLink}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      copied 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-[#B46C4A] hover:bg-[#9B5C3E] text-white'
                    }`}
                    data-testid="copy-link-btn"
                  >
                    {copied ? (
                      <>
                        <CheckCircle size={18} weight="fill" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Copy size={18} />
                        Copy
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* Share Options Grid */}
              <div className="p-6">
                <p className="text-sm font-medium text-[#5C564F] mb-4">Share via</p>
                <div className="grid grid-cols-3 gap-4">
                  {shareOptions.map((option) => (
                    <a
                      key={option.name}
                      href={option.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex flex-col items-center gap-2 p-3 rounded-xl hover:bg-[#F2EBE1] transition-colors"
                      data-testid={`share-${option.name.toLowerCase()}`}
                    >
                      <div 
                        className="w-12 h-12 rounded-full flex items-center justify-center"
                        style={{ backgroundColor: `${option.color}20` }}
                      >
                        <option.icon size={24} weight="fill" style={{ color: option.color }} />
                      </div>
                      <span className="text-xs text-[#5C564F]">{option.name}</span>
                    </a>
                  ))}
                </div>
              </div>

              {/* Message */}
              <div className="px-6 pb-6">
                <div className="bg-[#F2EBE1] rounded-xl p-4 text-center">
                  <p className="text-sm text-[#5C564F]">
                    💚 Your share could help someone who's struggling. Thank you for spreading the word!
                  </p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ShareButton;
